import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classNames from 'classnames';
import {
  CircularProgressbarWithChildren,
  buildStyles
} from 'react-circular-progressbar';
import './styles.css';
import { Theme } from 'helpers/theme';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100px',
    cursor: 'pointer',
    padding: 2
  },
  innerContainer: {
    width: '70%',
    cursor: 'pointer'
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },
  heading: {
    fontSize: theme.fontSizes.small,
    fontWeight: '300',
    color: theme.colors.textGrey
  },
  statValue: {
    fontSize: theme.fontSizes.large,
    fontWeight: '700',
    color: theme.colors.primaryBlack
  },
  outerBar: {},
  innerBar: {}
}));

const outerGraph = (theme: any, hover: boolean) =>
  buildStyles({
    pathTransitionDuration: 1,
    pathColor: hover ? 'rgb(91 91 91)' : theme.colors.primaryGreen,
    trailColor: theme.colors.borderGrey
  });

const innerGraph = (theme: any, hover: boolean) =>
  buildStyles({
    rotation: 0.5,
    pathTransitionDuration: 1,
    pathColor: hover ? 'rgb(91 91 91)' : theme.colors.primaryRed,
    trailColor: theme.colors.borderGrey
  });

type Props = {
  innerValue: number;
  outerValue: number;
  className?: string;
  onHoverInner?: (hover: boolean) => void;
  onHoverOuter?: (hover: boolean) => void;
  onClickInner?: () => void;
  onClickOuter?: () => void;
};

function Graph({
  innerValue,
  outerValue,
  className,
  onHoverInner,
  onHoverOuter,
  onClickInner,
  onClickOuter
}: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });
  const [percentage, setPercentage] = React.useState(0);
  const [inner, setInner] = React.useState(0);
  const [outer, setOuter] = React.useState(0);
  const [hoverOuter, setHoverOuter] = React.useState(false);
  const [hoverInner, setHoverInner] = React.useState(false);

  const updateInnerHover = (val: boolean) => {
    setHoverInner(val);
    onHoverInner && onHoverInner(val);
  };

  const updateOuterHover = (val: boolean) => {
    setHoverOuter(val);
    onHoverOuter && onHoverOuter(val);
  };

  setTimeout(() => {
    // Check bottom half if not 0
    const val =
      innerValue + outerValue > 0 ? outerValue / (innerValue + outerValue) : 1;
    setPercentage(val * 100);
    setInner(innerValue);
    setOuter(outerValue);
  }, 100);

  return (
    <div
      className={classNames(classes.root, className)}
      onMouseEnter={(evt) => {
        evt.stopPropagation();
        updateOuterHover(true);
      }}
      onMouseLeave={() => updateOuterHover(false)}
      onClick={(evt) => {
        evt.stopPropagation();
        onClickOuter && onClickOuter();
      }}
    >
      <CircularProgressbarWithChildren
        value={outer}
        strokeWidth={7}
        maxValue={innerValue + outerValue}
        styles={outerGraph(theme, hoverOuter && !hoverInner)}
      >
        <div
          className={classNames(classes.innerContainer)}
          onMouseEnter={(evt) => {
            evt.stopPropagation();
            updateInnerHover(true);
          }}
          onMouseLeave={(evt) => {
            evt.stopPropagation();
            updateInnerHover(false);
          }}
          onClick={(evt) => {
            evt.stopPropagation();
            onClickInner && onClickInner();
          }}
        >
          <CircularProgressbarWithChildren
            value={inner}
            strokeWidth={10}
            maxValue={innerValue + outerValue > 0 ? innerValue + outerValue : 1}
            styles={innerGraph(theme, hoverInner)}
          />
        </div>
      </CircularProgressbarWithChildren>
    </div>
  );
}

export default Graph;
